import Link from "next/link";
import Head from "next/head";
import NextScript from "next/script";

import { constants } from "src/shared/constants";
import { useTeamConfig } from "src/components/reusable/components/TeamConfigProvider";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "Which events are included in the Paw Pass?",
    answer:
      "All non-conference home football games (excluding South Carolina), most men's basketball and baseball games and all women's basketball, women's gymnastics, softball and men's soccer. You can also secure a seat for home conference football games and South Carolina games, as well as premier basketball and baseball games, for an additional fee.",
  },
  {
    question: "When can I start making reservations for specific games?",
    answer:
      "You can begin reserving games for the 2026-27 home games starting in August 2026.",
  },
  {
    question: "How do Paw Pass reservations work?",
    answer:
      "With a Paw Pass, you can hold up to three (3) game reservations at any given time. Once one of your reserved games occurs, the reservation reopens and can be applied to a future game.",
  },
  {
    question: "Can I have multiple subscriptions on my account?",
    answer:
      "Yes, an account can have multiple Paw Passes, a limit of 8 per account.",
  },
  {
    question: "How much does the Paw Pass cost?",
    answer:
      "The Paw Pass is available as a monthly subscription at $31.99 per month (August through May) or as an annual plan at $299.",
  },
  {
    question: "Can a game sell out?",
    answer:
      "Yes, if all inventory is exhausted for a select game, the site will show that it is sold out. We would recommend checking back to see if the game becomes available later.",
  },
  {
    question: "When will I be charged for my monthly Paw Pass?",
    answer:
      "Your credit card will be charged on the 26th of each month. If your card is declined, a representative will reach out to update it. If the card is not updated, future reservations will automatically be canceled.",
  },
  {
    question: "How do I access my barcodes to enter games?",
    answer:
      "Barcodes to gain entry to games will appear at noon (EST) the day prior to gameday.",
  },
  {
    question: "Where do I make reservations?",
    answer: (
      <>
        You can manage your Paw Pass account by logging in{" "}
        <Link
          target="_blank"
          href="https://pass.attendevents.com/clemsontigers_pass/landing"
        >
          here
        </Link>
        .
      </>
    ),
  },
  {
    question: "What if I can't attend a game I reserved?",
    answer:
      "If you are unable to attend a reserved game, please cancel the reservation as early as possible so the seat can be made available to other pass holders.",
  },
  {
    question: "Are Paw Pass reservations transferable or sellable?",
    answer:
      "No. Reservations and seats are for the exclusive use of the individual member – they are NOT transferable or re-sellable.",
  },
  {
    question: "What do I do if I need ADA seating?",
    answer:
      "Members who require ADA seating can indicate this within their account settings, and ADA seats will be automatically filtered during the seat reservation process. An ADA seat filter can also be manually selected during the seat reservation process. If you have any ADA-related questions or there are no ADA seats available for an event you would like to attend, please contact the Clemson Ticket Office at cutixonline@clemson.edu or (864) 656-2955.",
  },
  {
    question: "Can I get a refund for the Paw Pass?",
    answer:
      "Paw Pass purchases are non-refundable. For questions regarding your subscription, please reach out to Clemson Athletics support.",
  },
  {
    question: "Can I cancel my monthly renewal at any time?",
    answer:
      "Yes, you can cancel your renewal under the account tab on the home page.",
  },
];

export const REGISTER_TERMS_AND_CONDITION = () => (
  <>
    By creating an account, you agree to receive account-related notification
    and marketing communication from the Clemson University and you confirm that
    you have read and agree to the platform&apos;s{" "}
    <Link target="_blank" href="https://www.attend.tech/terms-of-use">
      Terms &amp; Conditions
    </Link>{" "}
    and{" "}
    <Link target="_blank" href="https://www.attend.tech/privacy-policy">
      Privacy Policy
    </Link>
    .
  </>
);

export const TERMS_AND_CONDITION = () => (
  <>
    By checking the box you agree to receive notifications regarding your
    purchase and to the{" "}
    <Link target="_blank" href="https://www.attend.tech/terms-of-use">
      Terms &amp; Conditions
    </Link>{" "}
    and{" "}
    <Link target="_blank" href="https://www.attend.tech/privacy-policy">
      Privacy Policy
    </Link>
    .
  </>
);

export const AnalyticsHeader = () => {
  const { TEAM_SCHEMA } = useTeamConfig();

  return (
    <Head>
      {/* Meta Pixel */}
      <script
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        defer
        id={`${TEAM_SCHEMA}-pixel-script`}
        dangerouslySetInnerHTML={{
          __html: `
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '1411668867137634');
            fbq('track', 'PageView');
            window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
              try{
                const { cartRes, checkoutRes, passes } = e.detail;

                let content_name = "";
                if(cartRes.products && cartRes.products.length) {
                  content_name = "${TEAM_SCHEMA}_single_game_purchases";
                };
                if(cartRes.passes && cartRes.passes.length && passes.length ) {
                  const passDetail = passes.find((pass) => pass.passId == cartRes.passes[0].passId);
                  content_name = passDetail.passTitle;
                };

                fbq('track', 'Purchase', {
                  content_name: content_name,
                  value: String(cartRes.totalAmount),
                  currency: 'USD',
                  order_id: checkoutRes.orderNumber,
                });
                console.log('meta pixel purchase event fired.');

              } catch(error){
                console.log('meta pixel purchase event error:', error);
              }
            });
          `,
        }}
      />
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          src="https://www.facebook.com/tr?id=1411668867137634&ev=PageView&noscript=1"
        />
      </noscript>

      {/* Google Analytics 4 */}
      <NextScript
        async
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        src="https://www.googletagmanager.com/gtag/js?id=G-Y3KHDXKEW0"
      />
      <script
        id={`${TEAM_SCHEMA}-analytics-inline`}
        nonce={process.env.NEXT_PUBLIC_CSP_NONCE}
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag() {
              dataLayer.push(arguments);
            }
            gtag('js', new Date());
            gtag('config', 'G-Y3KHDXKEW0');
            window.addEventListener("${constants.customEvents.purchaseSuccess}", function(e) {
              try {
                const { cartRes, checkoutRes } = e.detail;
                gtag('event', 'purchase', {
                  'send_to': 'G-Y3KHDXKEW0',
                  'value': cartRes.totalAmount,
                  'currency': 'USD',
                  'transaction_id': checkoutRes.orderNumber
                });
                console.log('gtag analytics purchase event fired.');
              } catch (error) {
                console.log('gtag analytics purchase event error:', error);
              }
            });
          `,
        }}
      />
    </Head>
  );
};
